import React, { useState, useEffect, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import addDays from 'date-fns/addDays';
import parseISO from 'date-fns/parseISO';
import { Modal, ModalBody } from 'react-bootstrap';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave, faTimesCircle } from '@fortawesome/free-solid-svg-icons'
import { ResponseSuccessModal, ResponseFailModal } from '../../modal/Response';
import Spin from '../../Spin';
import countryInfo from '../../../assets/countries.json';
import * as api from '../../../api';

const BookingUpdate = ({
  isOpen,
  toggle,
  reload,
  bookingId,
}) => {
  const { t } = useTranslation();
  const countryInfoList = Object.values(countryInfo);
  const [ isLoading, setIsLoading ] = useState(false);
  const [ countryNumber, setCountryNumber ] = useState('82');
  const [ isOpenBookingUpdateSuccessModal, setIsOpenBookingUpdateSuccessModal ] = useState(false);
  const [ isOpenBookingUpdateFailModal, setIsOpenBookingUpdateFailModal ] = useState(false);
  const [ messageBookingUpdateFail, setMessageBookingUpdateFail ] = useState('');
  const [ bookingUpdateItem, setBookingUpdateItem ] = useState({
    userName: '',
    phoneNumber: '',
    desc: '',
    checkinAt: '',
    checkoutAt: '',
  });

  const setMaxDate = () => {
    return addDays(parseISO(bookingUpdateItem.checkinAt), 15);
  };

  const updateBooking = async () => {
    try {
      setIsLoading(true);
      const reqNum = countryNumber + Number.parseInt(bookingUpdateItem.phoneNumber);
      await api.updateBooking({bookingId, ...bookingUpdateItem, phoneNumber:reqNum});
      toggle();
      setIsOpenBookingUpdateSuccessModal(true);
      setTimeout(() => {
        setIsOpenBookingUpdateSuccessModal(false);
        reload();
      }, 1500);
    } catch (error) {
      if (!error.response) setMessageBookingUpdateFail(error.message);
      else setMessageBookingUpdateFail(`${error.response.data.code}, ${error.response.data.message}`);
      setIsOpenBookingUpdateFailModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const selectBooking = async () => {
      try {
        const res = await api.selectBooking({bookingId});
        setBookingUpdateItem({
          userName: res.data.user.name,
          phoneNumber: res.data.user.phone_number.replace(/([0-9]*)(\d{10})/, '0$2'),
          desc: res.data.desc || '',
          checkinAt: new Date(res.data.checkin_at),
          checkoutAt: new Date(res.data.checkout_at),
        });
        setCountryNumber(res.data.user.phone_number.replace(/([0-9]*)(\d{10})/, '$1'));
      } catch (error) {
        throw error;
      }
    };
    if (bookingId) selectBooking();
  }, [bookingId, isOpen]);

  return (
    <Fragment>
      <Modal
        show={isOpen}
        onHide={() => toggle()}
        centered
        backdrop='static'
        size='md'
      >
        <Spin isSpin={isLoading} isModal={true}/>
        <ModalBody className='text-center m-3' style={{margin:0, padding:0, backgroundColor:'#282C34'}}>
          <div className='form-card'>
            <div className='form-card-title'>
              <h4>{t('content_title.booking_edit')}</h4>
            </div>
            <div className='inline-flex'>
              <div className='w-100'>
                <div className='card-text'>
                  <p>
                    {/* <FontAwesomeIcon icon={faUserAlt}/> */}
                    <b>{t('data_column.user_name')}</b>
                    <span className='required'>*</span>
                  </p>
                  <input
                    className='card-text-input'
                    placeholder={t('placeholder.user_name')}
                    value={bookingUpdateItem.userName}
                    onChange={e => setBookingUpdateItem({...bookingUpdateItem, userName:e.target.value})}
                  />
                </div>
                <div className='card-text'>
                  <p>
                    {/* <FontAwesomeIcon icon={faPhone}/> */}
                    <b>{t('data_column.phone_number')}</b>
                    <span className='required'>*</span>
                  </p>
                  <select
                    className='card-text-select'
                    value={countryNumber}
                    onChange={e => setCountryNumber(e.target.value)}
                  >
                    {countryInfoList.map((country, index) => (
                      <option key={`country-index-${index}`} value={country.phone}>{`${country.name} ( +${country.phone} )`}</option>
                    ))}
                  </select>
                  <p></p>
                  <input
                    className='card-text-input'
                    placeholder={t('placeholder.phone_number')}
                    value={bookingUpdateItem.phoneNumber}
                    maxLength='11'
                    onChange={e => setBookingUpdateItem({...bookingUpdateItem, phoneNumber:e.target.value})}
                  />
                </div>
                <div className='card-text'>
                  <p>
                    {/* <FontAwesomeIcon icon={faHourglassStart}/> */}
                    <b>{t('data_column.checkin_at')}</b>
                    <span className='required'>*</span>
                  </p>
                  <DatePicker
                    dateFormat='yyyy-MM-dd HH:mm'
                    placeholderText={t('placeholder.checkin_at')}
                    selected={bookingUpdateItem.checkinAt}
                    selectsStart
                    minDate={new Date()}
                    endDate={bookingUpdateItem.checkoutAt}
                    showTimeSelect
                    timeFormat='HH:mm'
                    timeIntervals={60}
                    timeCaption='Time'
                    onChange={date => setBookingUpdateItem({...bookingUpdateItem, checkinAt:date})}
                    withPortal
                  />
                </div>
                <div className='card-text'>
                  <p>
                    {/* <FontAwesomeIcon icon={faHourglassEnd}/> */}
                    <b>{t('data_column.checkout_at')}</b>
                    <span className='required'>*</span>
                  </p>
                  <DatePicker
                    disabled={!bookingUpdateItem.checkinAt}
                    dateFormat='yyyy-MM-dd HH:mm'
                    placeholderText={t('placeholder.checkout_at')}
                    selected={bookingUpdateItem.checkoutAt}
                    selectsEnd
                    minDate={bookingUpdateItem.checkinAt}
                    maxDate={setMaxDate()}
                    startDate={bookingUpdateItem.checkinAt}
                    showTimeSelect
                    timeFormat='HH:mm'
                    timeIntervals={60}
                    timeCaption='Time'
                    onChange={date => setBookingUpdateItem({...bookingUpdateItem, checkoutAt:date})}
                    withPortal
                  />
                </div>
                <div className='card-text'>
                  <p>
                    {/* <FontAwesomeIcon icon={faClipboard}/> */}
                    <b>{t('data_column.description')}</b>
                  </p>
                  <input
                    className='card-text-input'
                    placeholder={t('placeholder.description_booking')}
                    value={bookingUpdateItem.desc}
                    onChange={e => setBookingUpdateItem({...bookingUpdateItem, desc:e.target.value})}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className='card-foot'>
            <div className='form-item-cancel'>
              <button className='btn-item-cancel' onClick={() => toggle()}>
                <FontAwesomeIcon icon={faTimesCircle} style={{paddingRight:'3px'}}/><span>{t('button.cancel')}</span>
              </button>
            </div>
            <div className='form-item-add center'>
              <button className='btn-item-add' onClick={updateBooking}>
                <FontAwesomeIcon icon={faSave} style={{paddingRight:'2px'}}/><span>{t('button.save')}</span>
              </button>
            </div>
          </div>
        </ModalBody>
      </Modal>
      <ResponseSuccessModal
        isOpen={isOpenBookingUpdateSuccessModal}
        toggle={() => setIsOpenBookingUpdateSuccessModal(!isOpenBookingUpdateSuccessModal)}
        message='예약 정보 수정이 완료 되었습니다.'
      />
      <ResponseFailModal
        isOpen={isOpenBookingUpdateFailModal}
        toggle={() => setIsOpenBookingUpdateFailModal(!isOpenBookingUpdateFailModal)}
        message={messageBookingUpdateFail || '예약 정보 수정에 실패 하였습니다.'}
      />
    </Fragment>
  );
};

export default BookingUpdate;